import { useCallback } from "react";
import { db } from "../../db";
import { draft } from "../../hooks/draft";
import { useMainContext } from "../../hooks/useMainContext";
import {
  changeInScope,
  editAllInScope,
  editInScope,
  findStoredRow,
  refusalMessage,
  scopeLabel
} from "./scopeEdit";

const propertyTitle = (id) => db.peek("properties", id)?.title;

// The stored row of accessor:id (not the draft one). Read only.
export const storedRow = (accessor, id) =>
  findStoredRow(draft, accessor, id, (table, key) => db.peek(table, key));

// The warning of a refused edit (none on main: the store notified it).
function useRefusal() {
  const { createNotification } = useMainContext();

  return useCallback(
    (result, { accessor, id, label }) => {
      if (result.reason === "main") return;
      if (result.reason === "error") console.warn(result.error);

      createNotification(
        "warning",
        refusalMessage(
          {
            ...result,
            scopeLabel: scopeLabel(result.scope ?? "", propertyTitle)
          },
          { accessor: result.accessor ?? accessor, id: result.id ?? id, label }
        )
      );
    },
    [createNotification]
  );
}

// edit(accessor, id, updater, { label }) => the draft row, or false: draft.edit
// in the scope of the page; when the row can't be drafted here (unsaved
// changes in another scope, the row of an open form, main) nothing changes
// and the user is told why.
export default function useScopeEdit(scope) {
  const refuse = useRefusal();

  return useCallback(
    (accessor, id, updater, { label } = {}) => {
      const result = editInScope(draft, scope, accessor, id, updater);
      if (result.ok) return result.row;

      refuse(result, { accessor, id, label });
      return false;
    },
    [refuse, scope]
  );
}

// editAll(edits, { label }) => the draft rows, or false: several rows of one
// operation ([{ accessor, id, updater(row, prior) }]), all drafted or none.
export function useScopeEditAll(scope) {
  const refuse = useRefusal();

  return useCallback(
    (edits, { label } = {}) => {
      const result = editAllInScope(draft, scope, edits);
      if (result.ok) return result.rows;

      refuse(result, { ...edits[0], label });
      return false;
    },
    [refuse, scope]
  );
}

// change(ops, { label, closing, dryRun }) => the Map of the rows (key ->
// row, null for a remove), or false: draft.change in the scope of the page,
// the rows of one operation created, edited and removed together (all or
// none). When one of them can't be changed here (unsaved changes in another
// scope, the row of an open form, a row not found or that exists, main)
// nothing changes and the user is told why. label: the name of the rows
// ("Variant"), or one per accessor ({ blocks: "Variant", endpoints:
// "Endpoint" }).
export function useScopeChange(scope) {
  const refuse = useRefusal();

  return useCallback(
    (ops, { label, ...options } = {}) => {
      const result = changeInScope(draft, scope, ops, options);
      if (result.ok) return result.rows;

      const [first] = ops;
      const accessor = result.accessor ?? first?.accessor;
      const id =
        result.id ?? (first?.kind === "create" ? first?.row?.id : first?.id);
      refuse(result, {
        accessor,
        id,
        label: label && typeof label === "object" ? label[accessor] : label
      });
      return false;
    },
    [refuse, scope]
  );
}
